import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { WebBlePrintersState, WebBlePrintSettings } from './webBlePrinters.types';

// Modelo DINÁMICO de impresoras Web Bluetooth (BLE): un Record keyed por id de
// perfil, con altas/bajas. Espeja el slice de Electron (`electronPrintSettings`)
// para que los consumidores iteren `Object.keys(settings)` sin asumir keys fijas.
//
// Persistencia: blob JSON en localStorage bajo una llave DEDICADA
// (`webBlePrintersConfig`). Esa llave NO está en la allowlist de borrado del
// logout ni se toca en `resetStore` (el slice debe registrarse en
// DEVICE_SCOPED_SLICES del store), por lo que el perfil sobrevive login/logout
// del cajero — es propiedad del EQUIPO físico, no del usuario.
//
// A diferencia de Electron, NO hay migración de slots legacy: Web BLE es un
// camino nuevo. Un mapa vacío es un estado inicial válido (sin impresoras
// emparejadas todavía); el camino BLE es opt-in (decisión de arquitectura).

export type {
  WebBleAutoPrint,
  WebBlePageWidth,
  WebBlePrintersState,
  WebBlePrintSettings,
  WebBleTextSize,
  WebBleTicketType,
} from './webBlePrinters.types';

const CONFIG_KEY = 'webBlePrintersConfig';

const safeGet = (key: string): string | null => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) return localStorage.getItem(key);
  } catch {
    /* localStorage no disponible (tests/SSR) */
  }
  return null;
};

const safeSet = (key: string, value: string) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) localStorage.setItem(key, value);
  } catch {
    /* no-op */
  }
};

export const makeWebBlePrinterId = (): string =>
  'wb' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

export const defaultWebBlePrintSettings = (patch: Partial<WebBlePrintSettings> = {}): WebBlePrintSettings => ({
  deviceId: null,
  deviceName: null,
  label: '',
  enabled: true,
  showLogo: true,
  ticketType: 'normal',
  textSize: 'medium',
  pageWidth: 80,
  decoration: true,
  leftMargin: 0,
  rightMargin: 0,
  copies: 1,
  autoPrint: 'disabled',
  ...patch,
});

const persist = (state: WebBlePrintersState) => safeSet(CONFIG_KEY, JSON.stringify(state));

const initialState = (): WebBlePrintersState => {
  const blob = safeGet(CONFIG_KEY);
  if (blob) {
    try {
      const parsed = JSON.parse(blob) as WebBlePrintersState;
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      /* blob corrupto → mapa vacío */
    }
  }
  // Mapa vacío válido: el camino BLE es opt-in y arranca sin impresoras.
  return {};
};

export const webBlePrintersSlice = createSlice({
  name: 'webBlePrinters',
  initialState: initialState(),
  reducers: {
    updateWebBlePrintSettings: (
      state,
      action: PayloadAction<{ settings: Partial<WebBlePrintSettings>; printerKey: string }>,
    ) => {
      const { settings, printerKey } = action.payload;
      if (!state[printerKey]) return state;
      const next = { ...state, [printerKey]: { ...state[printerKey], ...settings } };
      persist(next);
      return next;
    },
    addPrinter: (state, action: PayloadAction<{ id?: string; patch?: Partial<WebBlePrintSettings> } | undefined>) => {
      const id = action.payload?.id || makeWebBlePrinterId();
      const next = { ...state, [id]: defaultWebBlePrintSettings(action.payload?.patch) };
      persist(next);
      return next;
    },
    removePrinter: (state, action: PayloadAction<{ printerKey: string }>) => {
      if (!state[action.payload.printerKey]) return state;
      const next = { ...state };
      delete next[action.payload.printerKey];
      persist(next);
      return next;
    },
    reset: () => {
      // El mapa vacío es el estado fresco. resetStore NO debe invocar esto (el
      // slice es device-scoped); existe por simetría con el resto de slices.
      const fresh: WebBlePrintersState = {};
      persist(fresh);
      return fresh;
    },
  },
});
